import { Box, Button, Checkbox, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Paper, Popover, Select, SelectChangeEvent, Stack, styled, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography, useTheme } from '@mui/material';
// import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
// import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { collection, doc, DocumentData, endBefore, getCountFromServer, getDoc, getDocs, getFirestore, limit, limitToLast, orderBy, Query, query, QueryDocumentSnapshot, startAfter, startAt, where } from 'firebase/firestore';
import { DateTime } from 'luxon';
import React, { ChangeEvent, MouseEvent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TimeClock } from '../../types/TimeClock';
import { queryEmployee } from '../../utils/queryEmployee';
import { queryTimeClocks } from '../../utils/queryTimeClocks';
import { CleanButton } from '../CleanButton';
import { CSVLink } from 'react-csv';
import { useDebouncedCallback } from 'use-debounce';
import timeClockConverter from '../../converters/timeClockConverter';
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions';
import { Search, SearchIconWrapper, StyledInputBase } from '../Search';
import { PersonSearchRounded, AddRounded, MopedRounded, RunCircleRounded, LocationOnRounded, RefreshRounded, ArrowForwardIosRounded, CalendarMonthRounded } from '@mui/icons-material';
import isDate from 'validator/lib/isDate';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { Employee } from '../../types/Employee';
import employeeConverter from '../../converters/employeeConverter';
import { AttendanceGeoLocationBox } from './AttendanceGeoLocationBox';

const StyledCSVLink = styled(CSVLink)(({theme}) => ({
  color: theme.palette.primary.dark,
  fontSize: 18,
  fontWeight: 700,
  textDecoration: 'none',
  '&:hover': {
    color: theme.palette.primary.main,
  },
  '&:active': {
    color: theme.palette.primary.dark,
  }
}));

type Attendance = {
  timeClock: TimeClock;
  status: string; // 'present' | 'absent';
};

export const AttendanceDataTable = () => {
  const db = getFirestore();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const [attendances, setAttendaces] = useState<Attendance[]>([]);
  const [attendancesFiltered, setAttendacesFiltered] = useState<Attendance[]>([]);

  const [search, setSearch] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRows, setTotalRows] = useState(0);

  const [loading, setLoading] = useState(true);
  const [dateAnchorEl, setDateAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedTimeClock, setSelectedTimeClock] = useState<TimeClock | null>(null);

  let paramDate = searchParams.get('date') ?? DateTime.now().toFormat('yyyyMMdd');
  const paramFilter = searchParams.get('filter') ?? 'present';

  if (paramDate.trim() === '')
    paramDate = DateTime.now().toFormat('yyyyMMdd');
  else if (!DateTime.fromFormat(paramDate, 'yyyyMMdd').isValid)
    paramDate = DateTime.now().toFormat('yyyyMMdd');

  const csvHeader = [
    { label: 'Id Number', key: 'idNumber' },
    { label: 'Name', key: 'name' },
    { label: 'Time In', key: 'timeIn' },
    { label: 'Time Out', key: 'timeOut' },
  ];

  const refresh = () => {
    setPage(0);

    setLoading(true);
    (async () => {
      const employees = (await getDocs(
        query(collection(db, `Employees`), where('department', '==', 'AL NADAFA'), where('status', '==', 'Active'), orderBy('idNumber', 'asc')).withConverter(employeeConverter))
      ).docs.map(v => v.data());
      const employeeTimeClocks = (await getDocs(
        query(query(collection(db, `TimeClocks/${paramDate}/Employees`), orderBy('idNumber', 'asc')).withConverter(timeClockConverter)))
      ).docs.map(v => v.data());

      return employees.map<Attendance>(e => {
        const tc = employeeTimeClocks.find(t => t.idNumber === e.idNumber);
        return {
          timeClock: tc ?? {
            idNumber: e.idNumber,
            name: e.name,
            timeIn: '',
            timeOut: '',
          },
          status: tc === undefined ? 'absent' : 'present',
        };
      });
    })().then(result => {
      const filtered = result.filter(a => (a.status === paramFilter || paramFilter === 'all') && a.timeClock.idNumber.includes(search));
      
      setAttendaces(result);
      setAttendacesFiltered(filtered.slice(0, rowsPerPage));
      setTotalRows(filtered.length);
    }).finally(() => setLoading(false));
  };

  const handleSearchOnChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.currentTarget.value);
  };

  const handleToggleFilterChange = (_e: React.MouseEvent<HTMLElement>, selectedValue: string | null) => {
    if (selectedValue !== null) {
      setSearchParams({
        date: paramDate,
        filter: selectedValue,
      });
    }
  };

  const handleOpenDate = (event: React.MouseEvent<HTMLElement>) => {
    setDateAnchorEl(event.currentTarget);
  };

  const handleCloseDate = () => {
    setDateAnchorEl(null);
  };

  const handleCalendarOnChange = (selectedDate: DateTime | null) => {
    if (selectedDate !== null) {
      setSearchParams({
        date: selectedDate.toFormat('yyyyMMdd'),
        filter: paramFilter,
      });
      setDateAnchorEl(null);
    }
  };

  const handleEmployeeNameOnClick = (event: MouseEvent<HTMLButtonElement>, idNumber: string) => {
    event.stopPropagation();
    queryEmployee(idNumber).then((employee) => {
      if (employee) {
        navigate(`/home/employees/${employee.idNumber}`, {
          state: {
            employee,
          }
        });
      }
    });
  };

  const handlePageOnChange = (_e: React.MouseEvent<HTMLButtonElement> | null, to: number) => {
    setPage(to);
  };
  
  const handleRowsPerPageOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
  };

  useEffect(() => {
    refresh();
  }, [paramDate]);

  useEffect(() => {
    const filtered = attendances.filter(a => (a.status === paramFilter || paramFilter === 'all') && a.timeClock.idNumber.startsWith(search));
    setAttendacesFiltered(filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage))
  }, [page]);

  useEffect(() => {
    setPage(0);

    const filtered = attendances.filter(a => (a.status === paramFilter || paramFilter === 'all') && a.timeClock.idNumber.startsWith(search));
    setAttendacesFiltered(filtered.slice(0, rowsPerPage));
    setTotalRows(filtered.length);
  }, [paramFilter, search, rowsPerPage]);

  return (
    <>
      <Container disableGutters>
        <Stack direction='row' mb={1} spacing={1}>
          <Box width={650}>
            <Search>
              <SearchIconWrapper>
                <PersonSearchRounded color='primary' />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search employee by Id Number"
                inputProps={{ 'aria-label': 'search' }}
                value={search}
                onChange={handleSearchOnChanged}
              />
            </Search>
          </Box>
          <Box flexGrow={1} />
          <ToggleButtonGroup exclusive size='small' value={paramFilter} onChange={handleToggleFilterChange}>
            <ToggleButton value='all' sx={{ width: 80 }}>All</ToggleButton>
            <ToggleButton value='present' sx={{ width: 80 }}>Present</ToggleButton>
            <ToggleButton value='absent' sx={{ width: 80 }}>Absent</ToggleButton>
          </ToggleButtonGroup>
          <Button endIcon={<CalendarMonthRounded />} variant='outlined' onClick={handleOpenDate}>
            {DateTime.fromFormat(paramDate, 'yyyyMMdd').toFormat('MMMM dd, yyyy')}
          </Button>
        </Stack>
        <Paper sx={{ overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={76} align="right">Count</TableCell>
                  <TableCell width={120}>Id Number</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell width={220}>Time In</TableCell>
                  <TableCell width={220}>Time Out</TableCell>
                  <TableCell width={72} align='center' />
                  <TableCell width={72} align='center'>
                    <Tooltip title='Refresh'>
                      <IconButton color='info' onClick={refresh}><RefreshRounded /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell colSpan={6}>
                      <Box sx={{ height: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                        <CircularProgress />
                        <Typography variant='caption' sx={{ mt: 2 }}>Please wait...</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : attendancesFiltered.map((a, idx) => {
                  const tc = a.timeClock;
                  return (
                    <TableRow hover key={tc.idNumber} sx={{ cursor: 'pointer', '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell align='right'>{idx + 1}</TableCell>
                      <TableCell>{tc.idNumber}</TableCell>
                      <TableCell>
                        <CleanButton 
                          sx={{ 
                            color: (theme) => paramFilter === 'absent' 
                              ? theme.palette.error.dark
                              : theme.palette.primary.dark
                          }} 
                          onClick={(evt) => handleEmployeeNameOnClick(evt, tc.idNumber)}>
                          {tc.name}
                        </CleanButton>
                      </TableCell>
                      <TableCell>{tc.timeIn ? DateTime.fromISO(tc.timeIn).toLocaleString(DateTime.DATETIME_MED) : ''}</TableCell>
                      <TableCell>{tc.timeOut ? DateTime.fromISO(tc.timeOut).toLocaleString(DateTime.DATETIME_MED) : ''}</TableCell>
                      <TableCell>
                        <Tooltip title='View Map Location'>
                          <span>
                            <IconButton color='error' onClick={() => setSelectedTimeClock(tc)}><LocationOnRounded /></IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title='View'>
                          <IconButton color='info' onClick={() => navigate(`/home/attendance/${tc.idNumber}?date=${paramDate}`)}><ArrowForwardIosRounded /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            rowsPerPageOptions={[25, 50, 100]}
            count={totalRows}
            page={page}
            rowsPerPage={rowsPerPage ?? 25}
            SelectProps={{
              inputProps: {
                'aria-label': 'rows per page',
              },
            }}
            onPageChange={handlePageOnChange}
            onRowsPerPageChange={handleRowsPerPageOnChange}
            ActionsComponent={TablePaginationActions} />
        </Paper>
      </Container>
      <Popover
        id='popover-date-filter'
        open={Boolean(dateAnchorEl)}
        anchorEl={dateAnchorEl}
        onClose={handleCloseDate}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <DateCalendar value={DateTime.fromFormat(paramDate, 'yyyyMMdd')} onChange={handleCalendarOnChange} />
        </LocalizationProvider>
      </Popover>
      <Dialog maxWidth='lg' open={selectedTimeClock !== null} onClose={() => setSelectedTimeClock(null)} fullWidth>
        <Box p={3} py={1}>
          <Typography variant='h6'>
            Map Location
          </Typography>
          <Typography color='text.secondary' variant='caption'>
            {selectedTimeClock && `${selectedTimeClock.name} (${selectedTimeClock.idNumber})`}
          </Typography>
        </Box>
        <DialogContent dividers>
          {selectedTimeClock && (
            <AttendanceGeoLocationBox timeClock={selectedTimeClock} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedTimeClock(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
